import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View, Image, Animated, Platform, StyleSheet, StatusBar } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import MaskedView from '@react-native-masked-view/masked-view';
import { Svg, Mask, Rect } from 'react-native-svg';
import LinearGradient from 'utils/packages/linearGradient';
import { Camera, useCameraDevice } from 'utils/packages/Camera';
import { triggerHapticFeedback } from 'utils/hapticFeedback';
import Button from 'design-system/Button';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import { a, rem } from 'design-system/values';
import useFocusEffect from 'hooks/useFocusEffect';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import IconFlash from 'design-system/icons/IconFlash';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import { useHeaderHeight, useScreenDimensions } from 'hooks';
import { assertUnreachable } from 'utils/types';
import IconFlipCamera from '../icons/IconFlipCamera';
import { scanDocumentGradient } from '../colors';
import { VIEW_FINDER_HEIGHT, VIEW_FINDER_TOP_OFFSET } from '../constants';
import { isWeb } from '../../../constants';
import useCameraPermission from '../hooks/useCameraPermission';
import { KYCDocumentType } from '../types';
const strings = {
    navTitle: 'Verify Identity',
    confirmTitle: 'Is it easy to read?',
    upload: 'Continue',
    scanBackSide: 'Continue',
    retake: 'Retake',
    uploading: 'Uploading Photo...',
    verifyAddress: 'Continue',
    errorMessage: 'Oops! That didn’t go well, try again',
    noAccessTitle: 'Camera Check',
    noAccess: 'Access to the camera has been prohibited.\nPlease enable it in the settings to continue.',
    noAccessButton: 'Open settings',
};
const FACTOR = 1; // mask resolution factor
const VIEW_FINDER_HORIZONTAL_PADDING = rem(16);
const VIEW_FINDER_BORDER_RADIUS = 13; // no rem, because corners are SVG paths that have fixed shape which should match this corner radius
const DARK_OVERLAY_NEGATIVE_MARGIN = 1;
const getTitleForFront = (documentType) => {
    switch (documentType) {
        case KYCDocumentType.PASSPORT:
            return 'Full spread of the passport';
        case KYCDocumentType.DRIVING_LICENCE:
        case KYCDocumentType.PROVISIONAL_DRIVING_LICENCE:
            return 'Front of driving licence';
        case KYCDocumentType.NATIONAL_ID:
            return 'Front of identity card';
        case KYCDocumentType.UK_BIOMETRIC_RESIDENCE_PERMIT:
            return 'Front of residence permit';
        default:
            assertUnreachable(documentType);
            return '';
    }
};
const getDescForDocument = (documentType) => {
    switch (documentType) {
        case KYCDocumentType.PASSPORT:
            return 'Your entire passport (bottom and top pages) must be in the frame. Make sure that all the details are visible.';
        case KYCDocumentType.DRIVING_LICENCE:
        case KYCDocumentType.PROVISIONAL_DRIVING_LICENCE:
            return 'Your entire Driving License must be in the frame. Make sure all the details are visible.';
        case KYCDocumentType.NATIONAL_ID:
            return 'Your entire National ID must be in the frame. Make sure all the details are visible.';
        case KYCDocumentType.UK_BIOMETRIC_RESIDENCE_PERMIT:
            return 'Your entire ID must be in the frame. Make sure all the details are visible';
        default:
            assertUnreachable(documentType);
            return '';
    }
};
const getTitleForBack = (documentType) => {
    switch (documentType) {
        case KYCDocumentType.DRIVING_LICENCE:
        case KYCDocumentType.PROVISIONAL_DRIVING_LICENCE:
            return 'Back of driving licence';
        case KYCDocumentType.NATIONAL_ID:
            return 'Back of identity card';
        case KYCDocumentType.UK_BIOMETRIC_RESIDENCE_PERMIT:
            return 'Back of residence permit';
        default:
            return '';
    }
};
const ScanDocumentScreen = ({ route, navigation }) => {
    const flow = route.params?.flow;
    const photoFile = route.params?.photoFile;
    const documentType = route.params?.documentType;
    const sideToScan = photoFile ? 'back' : 'front';
    useLayoutEffect(() => {
        navigation.setOptions({
            title: strings.navTitle,
            headerRight: () => route.params?.showTorch ? (<NavigationHeaderRightIconButton onPress={() => {
                    ScanDocumentScreen.onToggleTorch();
                }}>
            <IconFlash />
          </NavigationHeaderRightIconButton>) : null,
        });
    }, [route.params?.showTorch]);
    const [focused, setFocused] = useState(false);
    const frontCamera = useCameraDevice('front');
    const backCamera = useCameraDevice('back');
    const [path, setPath] = useState(); // path to saved photo
    const [torch, setTorch] = useState('off');
    const [front, setFront] = useState(false); // front or back camera to use
    const camera = useRef(null);
    const blackOverlayOpacity = useRef(new Animated.Value(0));
    const buttonsContainerOpacity = useRef(new Animated.Value(0));
    const [accessAllowed, openSettings] = useCameraPermission();
    const { height: windowHeight, width: windowWidth } = useAppFrameDimensions(isWeb);
    const headerHeight = useHeaderHeight();
    const viewFinderTop = documentType === KYCDocumentType.PASSPORT
        ? headerHeight + rem(16)
        : (windowHeight - VIEW_FINDER_HEIGHT) / 2 - VIEW_FINDER_TOP_OFFSET;
    const viewFinderHeight = documentType === KYCDocumentType.PASSPORT ? (1.75 * windowHeight) / 3 : windowWidth * 0.62;
    const textTop = documentType === KYCDocumentType.PASSPORT
        ? viewFinderTop + viewFinderHeight
        : viewFinderTop + viewFinderHeight + rem(a(40, 40, 40, 80, 80));
    useFocusEffect(useCallback(() => {
        setFocused(true);
        setPath(undefined);
        blackOverlayOpacity.current.setValue(0);
        return () => {
            setFocused(false);
        };
    }, []));
    // useEffect(() => {
    //   const prepare = async () => {
    //     const devices = await Camera.getAvailableCameraDevices();
    //     // TODO: pick camera with best resolution
    //   };
    //   prepare();
    // }, []);
    useEffect(() => {
        if (front) {
            if (frontCamera?.hasTorch) {
                navigation.setParams({ showTorch: true });
            }
            else {
                navigation.setParams({ showTorch: false });
            }
        }
        else if (backCamera?.hasTorch) {
            navigation.setParams({ showTorch: true });
        }
        else {
            navigation.setParams({ showTorch: false });
        }
    }, [backCamera?.hasTorch, front, frontCamera?.hasTorch, path]);
    const animateTo = useCallback((value, callback) => {
        if (value === 1) {
            Animated.sequence([
                Animated.timing(blackOverlayOpacity.current, {
                    toValue: 1,
                    duration: 200,
                    useNativeDriver: true,
                }),
                Animated.timing(buttonsContainerOpacity.current, {
                    toValue: 1,
                    duration: 200,
                    useNativeDriver: true,
                }),
            ]).start(callback);
        }
        else if (value === 0) {
            Animated.sequence([
                Animated.timing(buttonsContainerOpacity.current, {
                    toValue: 0,
                    duration: 200,
                    useNativeDriver: true,
                }),
                Animated.timing(blackOverlayOpacity.current, {
                    toValue: 0,
                    duration: 200,
                    useNativeDriver: true,
                }),
            ]).start(callback);
        }
    }, []);
    const onTakePhoto = useCallback(async () => {
        if (camera.current) {
            triggerHapticFeedback('clockTick');
            const photo = await camera.current.takePhoto();
            const newFile = { path: `file://${photo.path.replace('file://', '')}`, width: photo.width, height: photo.height };
            setPath(newFile);
            animateTo(1, () => {
                navigation.navigateDeprecated('WealthConfirmDocumentScreen', {
                    flow,
                    documentType,
                    files: photoFile ? [newFile, photoFile] : [newFile],
                });
            });
        }
    }, [animateTo, documentType, flow, photoFile]);
    const onFlipCamera = useCallback(() => {
        setFront((currentState) => !currentState);
    }, []);
    ScanDocumentScreen.onToggleTorch = useCallback(() => {
        triggerHapticFeedback('clockTick');
        setTorch((currentState) => (currentState === 'on' ? 'off' : 'on'));
    }, []);
    const { width: WIDTH, height: HEIGHT } = useScreenDimensions();
    const { width: frameRealWidth, paddingHorizontal, paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const frameWidth = Platform.OS === 'ios' && Platform.isPad ? frameRealWidth : WIDTH;
    const maskElement = useMemo(() => {
        const y = `${(viewFinderTop + DARK_OVERLAY_NEGATIVE_MARGIN) * FACTOR}`;
        const x = `${(VIEW_FINDER_HORIZONTAL_PADDING + (paddingHorizontal || 0) + DARK_OVERLAY_NEGATIVE_MARGIN) * FACTOR}`;
        return (<Svg width={WIDTH + 2 * DARK_OVERLAY_NEGATIVE_MARGIN} height={HEIGHT + 2 * DARK_OVERLAY_NEGATIVE_MARGIN} viewBox={`0 0 ${(WIDTH + 2 * DARK_OVERLAY_NEGATIVE_MARGIN) * FACTOR} ${(HEIGHT + 2 * DARK_OVERLAY_NEGATIVE_MARGIN) * FACTOR}`} fill="none">
        <Mask id="mask" fill="white">
          <Rect x="0" y="0" width={`${(WIDTH + 2 * DARK_OVERLAY_NEGATIVE_MARGIN) * FACTOR}`} height={`${(HEIGHT + 2 * DARK_OVERLAY_NEGATIVE_MARGIN) * FACTOR}`} fill="white" // white will be shown
        />
          <Rect x={x} y={y} width={`${(frameWidth - VIEW_FINDER_HORIZONTAL_PADDING * 2) * FACTOR}`} height={`${viewFinderHeight * FACTOR}`} rx={`${VIEW_FINDER_BORDER_RADIUS * FACTOR}`} fill="black" // will be invisible (a hole) - view finder
        />
        </Mask>
        <Rect x="0" y="0" width={`${(WIDTH + 2 * DARK_OVERLAY_NEGATIVE_MARGIN) * FACTOR}`} height={`${(HEIGHT + 2 * DARK_OVERLAY_NEGATIVE_MARGIN) * FACTOR}`} fill="black" mask="url(#mask)"/>
      </Svg>);
    }, [HEIGHT, WIDTH, frameWidth, paddingHorizontal, viewFinderHeight, viewFinderTop]);
    const cameraView = useMemo(() => {
        if (accessAllowed === false) {
            return (<View style={[
                    styles.noAccessContainer,
                    {
                        paddingTop: viewFinderTop,
                    },
                ]}>
          <Text Text-20 White centered>
            {strings.noAccessTitle}
          </Text>
          <Spacer h={8}/>
          <Text TextThin-14 White centered>
            {strings.noAccess}
          </Text>
          <Spacer h={32}/>
          <Button brand style={styles.noAccessButton} title={strings.noAccessButton} onPress={openSettings}/>
        </View>);
        }
        if (Platform.OS === 'android' && accessAllowed === undefined) {
            return <View style={styles.noAccessContainer}/>; // on android, we need to wait until we are sure that we have access to camera
        }
        if (path) {
            return <Image style={StyleSheet.absoluteFill} source={{ uri: path.path }}/>;
        }
        if (front && frontCamera) {
            return (<Camera ref={camera} style={StyleSheet.absoluteFill} device={frontCamera} isActive={focused} photo torch={torch}/>);
        }
        if (backCamera) {
            return (<Camera ref={camera} style={StyleSheet.absoluteFill} device={backCamera} isActive={focused} photo torch={torch}/>);
        }
        return null;
    }, [accessAllowed, path, front, frontCamera, backCamera, viewFinderTop, openSettings, focused, torch]);
    const blackOverlayStyle = useMemo(() => [styles.overlay, styles.dark, { opacity: blackOverlayOpacity.current }], []);
    const bottomWithOffset = useMarginBottom('bottom', rem(30), styles.controlsContainer);
    const controlsContainerStyle = useMemo(() => [
        bottomWithOffset,
        {
            opacity: blackOverlayOpacity.current.interpolate({
                inputRange: [0, 1],
                outputRange: [1, 0],
            }),
        },
    ], [bottomWithOffset]);
    const title = useMemo(() => {
        if (sideToScan === 'back') {
            return getTitleForBack(documentType);
        }
        return getTitleForFront(documentType);
    }, [documentType, sideToScan]);
    const desc = useMemo(() => getDescForDocument(documentType), [documentType]);
    const controls = useMemo(() => (<Animated.View style={controlsContainerStyle} pointerEvents={path ? 'none' : 'auto'}>
        <View style={styles.shutterContainer}>
          <TouchableBounce style={styles.shutter} onPress={onTakePhoto}/>
        </View>
        <View style={styles.flipCameraContainer}>
          <TouchableBounce onPress={onFlipCamera}>
            <IconFlipCamera />
          </TouchableBounce>
        </View>
      </Animated.View>), [controlsContainerStyle, path, onTakePhoto, onFlipCamera]);
    return (<View style={[styles.container]}>
      <StatusBar barStyle="dark-content" animated/>
      {cameraView}
      <View style={styles.flex} pointerEvents={accessAllowed ? 'auto' : 'none'}>
        <MaskedView style={styles.maskedView} maskElement={maskElement}>
          {accessAllowed ? (<LinearGradient style={styles.overlayContent} {...scanDocumentGradient}>
              {controls}
            </LinearGradient>) : null}
          <Animated.View style={blackOverlayStyle} pointerEvents="none"/>
        </MaskedView>
      </View>
      <View style={[styles.textsContainer, paddingHorizontalStyle, { top: textTop }]} pointerEvents="none">
        <Text Text-20 White>
          {title}
        </Text>
        <Spacer h={8}/>
        <Text TextThin-14 White>
          {desc}
        </Text>
      </View>
    </View>);
};
const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#060606',
    },
    overlay: {
        ...StyleSheet.absoluteFillObject,
    },
    flex: {
        flex: 1,
    },
    maskedView: {
        position: 'absolute',
        top: -DARK_OVERLAY_NEGATIVE_MARGIN,
        bottom: -DARK_OVERLAY_NEGATIVE_MARGIN,
        left: -DARK_OVERLAY_NEGATIVE_MARGIN,
        right: -DARK_OVERLAY_NEGATIVE_MARGIN,
    },
    overlayContent: {
        ...Platform.select({
            ios: {
                flex: 1,
            },
            android: {
                ...StyleSheet.absoluteFillObject,
            },
        }),
    },
    dark: {
        backgroundColor: '#060606',
    },
    textsContainer: {
        position: 'absolute',
        padding: VIEW_FINDER_HORIZONTAL_PADDING,
    },
    controlsContainer: {
        position: 'absolute',
        left: 0,
        right: 0,
        alignItems: 'center',
    },
    shutterContainer: {
        width: rem(64),
        height: rem(64),
        borderRadius: rem(64),
        borderWidth: rem(4.5),
        borderColor: 'white',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#17102A',
    },
    shutter: {
        width: rem(50.6),
        height: rem(50.6),
        borderRadius: rem(50.6),
        backgroundColor: 'white',
    },
    flipCameraContainer: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: rem(16),
        justifyContent: 'center',
    },
    noAccessContainer: {
        flex: 1,
        paddingHorizontal: rem(16),
        backgroundColor: '#060606',
    },
    noAccessButton: {
        alignSelf: 'center',
        paddingHorizontal: rem(60),
    },
});
ScanDocumentScreen.onToggleTorch = () => { };
export default ScanDocumentScreen;
